import { MessageSquareWarningIcon } from "lucide-react";
import styled from "@emotion/styled";
import type { ComponentType, JSX } from "react";
import type { ExtraProps } from "react-markdown";
import ReactMarkdown from "react-markdown";
import { colors, media, spacing } from "../design-tokens";
import { LinkText, ParagraphSmall } from "../primitives/typographies";
import {
  buildPoweredByUrl,
  DISCLAIMER_LINK,
  DISCLAIMER_TEXT,
  POWERED_BY_TEXT,
} from "@/constants/disclaimer";
import { useWidgetRuntimeConfig } from "@/context/widget-runtime-config";
import { safeUrlTransform } from "@/utils/url-helpers";

const DisclaimerRowContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${spacing.md}px;
  width: 100%;

  ${media.md} {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    gap: ${spacing.lg}px;
  }
`;

const DisclaimerMessage = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing.sm}px;
  min-width: 0;
  flex: 1 1 auto;
`;

const DisclaimerText = styled.div`
  color: ${colors.neutralGrey};
  flex: 1 1 auto;
  box-sizing: border-box;
  margin: 0;
`;

const DisclaimerParagraph = styled(ParagraphSmall)`
  color: ${colors.neutralGrey};
  margin: 0;

  &:not(:first-of-type) {
    margin-top: ${spacing.xs}px;
  }
`;

const DisclaimerUnorderedList = styled.ul`
  margin: ${spacing.xs}px 0 0 0;
  padding-left: ${spacing.lg}px;
`;

const DisclaimerOrderedList = styled.ol`
  margin: ${spacing.xs}px 0 0 0;
  padding-left: ${spacing.lg}px;
`;

const DisclaimerListItem = styled.li`
  font-size: 14px;
  font-weight: 400;
  line-height: 120%;
  color: ${colors.neutralGrey};

  &:not(:first-of-type) {
    margin-top: ${spacing.xs}px;
  }
`;

const DisclaimerLink = styled(LinkText)`
  font-size: 14px;
  font-weight: 400;
  line-height: 120%;
  color: ${colors.neutralGrey};
  flex-shrink: 0;

  &:hover,
  &:visited,
  &:active {
    color: ${colors.neutralGrey};
  }
`;

type MarkdownLinkProps = JSX.IntrinsicElements["a"] & ExtraProps;

const DisclaimerMarkdownLink = ({
  node: _node,
  href,
  children,
  ...props
}: Readonly<MarkdownLinkProps>) => {
  const isExternalProtocol = href
    && (href.startsWith("tel:") || href.startsWith("mailto:"));

  if (isExternalProtocol) {
    return (
      <DisclaimerLink href={href} {...props}>
        {children}
      </DisclaimerLink>
    );
  }

  return (
    <DisclaimerLink href={href} target="_blank" rel="noopener noreferrer" {...props}>
      {children}
    </DisclaimerLink>
  );
};

const DisclaimerIcon = styled(MessageSquareWarningIcon)`
  width: 16px;
  height: 16px;
  color: ${colors.neutralGrey};
  flex-shrink: 0;
`;

const PoweredBy = styled(ParagraphSmall)`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.xs}px;
  color: ${colors.neutralGrey};
  flex-shrink: 0;
  white-space: nowrap;
  margin: 0;
`;

const PoweredByLink = styled(LinkText)`
  font-size: 14px;
  line-height: 120%;
  color: ${colors.neutralGrey};
  text-decoration-line: underline;

  &:hover {
    color: ${colors.text};
  }
`;

type Components = {
  [Key in keyof JSX.IntrinsicElements]?:
    | ComponentType<JSX.IntrinsicElements[Key] & ExtraProps>
    | keyof JSX.IntrinsicElements;
};

const disclaimerMarkdownComponents: Components = {
  p: DisclaimerParagraph,
  a: DisclaimerMarkdownLink,
  ul: DisclaimerUnorderedList,
  ol: DisclaimerOrderedList,
  li: DisclaimerListItem,
};

export const DisclaimerRow = () => {
  const { widgetDisclaimerText, organizationSlug } = useWidgetRuntimeConfig();
  const hasLink = Boolean(DISCLAIMER_LINK);
  const resolvedDisclaimerText = widgetDisclaimerText?.trim().length
    ? widgetDisclaimerText
    : DISCLAIMER_TEXT;
  const poweredByHref = buildPoweredByUrl(organizationSlug);

  return (
    <DisclaimerRowContainer>
      <DisclaimerMessage>
        <DisclaimerIcon size={16} aria-hidden="true" />
        <DisclaimerText>
          <ReactMarkdown
            components={disclaimerMarkdownComponents}
            urlTransform={safeUrlTransform}
          >
            {resolvedDisclaimerText}
          </ReactMarkdown>
          {hasLink && (
            <>
              &nbsp;
              <DisclaimerLink
                href={DISCLAIMER_LINK}
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more
              </DisclaimerLink>
            </>
          )}
        </DisclaimerText>
      </DisclaimerMessage>
      <PoweredBy>
        Powered by
        <PoweredByLink
          href={poweredByHref}
          target="_blank"
          rel="noopener noreferrer"
        >
          {POWERED_BY_TEXT}
        </PoweredByLink>
      </PoweredBy>
    </DisclaimerRowContainer>
  );
};
